import React, { useState } from 'react'
import Icon from 'components/elements/Icon'

import { useAppSelector } from 'app-redux/hooks'

import { CardType } from 'types/deck'

import CursorCardWrapper from 'components/card/CursorCardWrapper'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'

import DeckService from 'services/deck.service'
import SaveService from 'services/save.service'
import ToastService from 'services/toast.service'

import { timeSince } from 'utils/time.utils'

import styles from './addRemoveCardRow.module.scss'

type Props = {
  card: CardType
  onUpdateCardList: () => void
}

const AddRemoveCardRow = ({ card, onUpdateCardList }: Props) => {
  const [dirty, setDirty] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const categories = useAppSelector(state => state.deck.categories)

  const handleHardDelete = (card: CardType) => {
    DeckService.hardDeleteCard(card.deckRelationId)
      .then(() => {
        onUpdateCardList()

        ToastService.create(`Removed ${card.name} forever`, 'Deck history', 'success')
      })
      .catch(err => ToastService.create(err, 'Deck History', 'error'))
  }

  const handleAddBackToDeck = (card: CardType) => {
    const readdedCard = { ...card, deckRelationId: '' }

    SaveService.save(readdedCard)
      .then(() => setDirty(true))
      .catch(() => ToastService.create('Failed to add card back to deck', 'Deck history', 'error'))
  }

  const includedInDeck = categories[card.categories[0]]?.includedInDeck || false

  return (
    <>
      <tr>
        <td>
          <button onClick={() => setExpanded(!expanded)} className={styles.expandTrigger}>
            <Icon name="info circle" />
          </button>
        </td>
        <td className={styles.cardName}>
          <CursorCardWrapper card={card} className={`${styles.hoverCard} ${!card.name ? styles.italics : ''}`}>
            {card.name || 'Unknown or removed card'}
          </CursorCardWrapper>
        </td>
        <td>
          {dirty ? (
            <span className={styles.modified}>Outdated</span>
          ) : card.deletedAt ? (
            <span className={styles.removed}>Removed</span>
          ) : !includedInDeck ? (
            <span className={styles.notIncluded}>Added</span>
          ) : (
            <span className={styles.added}>Added</span>
          )}
        </td>
        <td className={styles.categories}>{card.categories.join(', ')}</td>
        <td className={styles.when}>
          {card.deletedAt || card.createdAt ? timeSince(card.deletedAt || card.createdAt || '') : 'N/A'}
        </td>
        <td>
          <ArchidektDropdown
            triggerClassName={styles.dropdown}
            menuClassName={styles.dropdownOptions}
            options={[
              {
                label: 'Remove forever',
                icon: 'trash',
                onClick: () => handleHardDelete(card),
                hidden: !card.deletedAt || dirty,
              },
              {
                label: 'Add back to deck',
                icon: 'add',
                onClick: () => handleAddBackToDeck(card),
                hidden: !card.deletedAt || dirty,
              },
              { label: 'Card Page', icon: 'external', to: `/card/?uid=${card.uid}` },
            ]}
          />
        </td>
      </tr>

      <tr className={`${expanded && styles.selected} ${styles.details}`}>
        {expanded && (
          <td colSpan={6}>
            <span className={styles.categoryDetails}>
              <h4>Categories</h4>
              <div>{card.categories.join(', ')}</div>
            </span>

            <h4>Detailed Times</h4>
            <div>
              <b>Added At:</b> {card.createdAt ? new Date(card.createdAt).toLocaleString() : 'N/A'}
            </div>
            <div>
              <b>Updated At:</b> {card.updatedAt ? new Date(card.updatedAt).toLocaleString() : 'N/A'}
            </div>
            <div>
              <b>Removed At:</b> {card.deletedAt ? new Date(card.deletedAt).toLocaleString() : 'N/A'}
            </div>
          </td>
        )}
      </tr>
    </>
  )
}

export default AddRemoveCardRow
